import { useEffect, useContext, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import ProductSidebar from '../components/ProductSidebar';
import Pagination from '../components/Pagination';
import WishlistButton from '../components/WishlistButton';
import FilterTags from '../components/FilterTags';
import { CartContext } from '../contexts/CartContext';
import productService from '../services/productService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import { formatPrice, getPrimaryImage, isInStock, getStockDisplay, handleImageError, addCacheBuster } from '../utils/apiHelpers';
import useContentUpdates from '../hooks/useContentUpdates';

const FALLBACK_BACKGROUND = '/assets/img/banner1.1.jpg';

function CategoryPage() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useContext(CartContext);

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed
  const [pageSize] = useState(12); // Items per page
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Filter state (for sidebar)
  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 800]);
  const [sortBy, setSortBy] = useState('default');
  const [filterLoading, setFilterLoading] = useState(false);
  const [sliderInstance, setSliderInstance] = useState(null);

  // Background image state
  const [backgroundUrl, setBackgroundUrl] = useState(FALLBACK_BACKGROUND);

  // Per-product image cache-busting keys for WebSocket updates
  const [productImageKeys, setProductImageKeys] = useState(new Map());

  // Fetch category by slug using direct API endpoint
  const fetchCategory = useCallback(async () => {
    try {
      const response = await productService.getCategoryBySlug(categorySlug);
      if (response.success && response.category) {
        setCategory(response.category);
        setError(null);
      } else {
        setCategory(null);
        setError(`Category "${categorySlug}" not found`);
      }
    } catch (err) {
      console.error('[CategoryPage] Error fetching category:', err);
      setError('Failed to load category');
    }
  }, [categorySlug]);

  // Fetch page background from the same source as product-listing
  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      } else {
        setBackgroundUrl(FALLBACK_BACKGROUND);
      }
    } catch (err) {
      console.error('[CategoryPage] Error fetching background:', err);
      setBackgroundUrl(FALLBACK_BACKGROUND);
    }
  }, []);

  // Fetch products for this category
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {
        category: categorySlug,
        page: currentPage,
        size: pageSize
      };

      // Add sort parameter if not default
      if (sortBy && sortBy !== 'default') {
        params.sortBy = sortBy;
      }

      // Add price range filter if active
      if (priceRange[0] > 0 || priceRange[1] < 800) {
        params.minPrice = priceRange[0] * 100; // Backend expects cents
        params.maxPrice = priceRange[1] * 100;
      }

      const response = await productService.getProducts(params);

      if (response.success) {
        setProducts(response.products || []);
        // Only update pagination if response contains it
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.error || 'Failed to load products');
      }
    } catch (err) {
      console.error('[CategoryPage] Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [categorySlug, currentPage, pageSize, priceRange, sortBy]); // Added sortBy to dependencies

  // Initial fetch on mount and when category slug changes
  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Listen for real-time content updates via WebSocket
  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    },

    onProductUpdated: (productId, sku, name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CategoryPage] Product ${productId} updated: ${name} (${sku}) - refreshing image for this product only`);
      }

      // Only refresh cache for this specific product
      setProductImageKeys(prev => {
        const newMap = new Map(prev);
        newMap.set(productId, Date.now());
        return newMap;
      });

      // Refetch only this specific product to get updated data
      productService.getProduct(productId).then(response => {
        if (response.success && response.product) {
          setProducts(prev =>
            prev.map(p => p.id === productId ? response.product : p)
          );
        }
      }).catch(err => {
        console.error('[CategoryPage] Error refetching product after update:', err);
        // Fallback: refetch all products if single product fetch fails
        fetchProducts();
      });
    },

    onProductDeleted: (productId) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CategoryPage] Product deleted: ${productId}`);
      }
      // Remove product from list immediately
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.warning('A product has been removed', { autoClose: 2000 });
    },

    onCategoryUpdated: (categoryId, slug, name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CategoryPage] Category updated: ${name}`);
      }

      // Always refetch categories list (for sidebar)
      fetchCategories();

      // If this is our category, refetch it and show toast
      if (slug === categorySlug) {
        fetchCategory();
        toast.info(`${name} category has been updated`, { autoClose: 2000 });
      }
    },

    onCategoryDeleted: (categoryId) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CategoryPage] Category deleted: ${categoryId}`);
      }

      // Always refetch categories list (for sidebar)
      fetchCategories();

      // If our current category was deleted, redirect to product listing
      if (category && category.id === categoryId) {
        toast.warning('This category has been removed', { autoClose: 3000 });
        setTimeout(() => navigate('/product-listing'), 1000);
      }
    }
  });

  // Fetch categories for sidebar
  const fetchCategories = useCallback(async () => {
    try {
      const response = await productService.getCategories();
      if (response.success) {
        setCategories(response.categories || []);
      }
    } catch (err) {
      console.error('[CategoryPage] Error fetching categories:', err);
    }
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Initialize price slider and other plugins
  useEffect(() => {
    const initPlugins = () => {
      if (window.$ && window.$.fn) {
        try {
          // Initialize Sticky sidebar
          if (window.Stickyfill) {
            const stickyElements = window.$('.sticky');
            if (stickyElements.length > 0) {
              window.Stickyfill.add(stickyElements);
            }
          }

          // Initialize noUiSlider for price range
          if (window.noUiSlider) {
            const rangeSlider = document.getElementById('slider-range');
            if (rangeSlider && !rangeSlider.noUiSlider) {
              window.noUiSlider.create(rangeSlider, {
                start: [0, 800],
                connect: true,
                range: {
                  'min': 0,
                  'max': 800
                }
              });

              setSliderInstance(rangeSlider);

              const minPrice = document.getElementById('min-p');
              const maxPrice = document.getElementById('max-p');

              // Update display values
              rangeSlider.noUiSlider.on('update', function (values, handle) {
                const value = values[handle];
                if (handle === 0) {
                  minPrice.textContent = '£' + Math.round(value);
                } else {
                  maxPrice.textContent = ' - £' + Math.round(value);
                }
              });

              // Update state when user releases slider
              rangeSlider.noUiSlider.on('change', function (values) {
                setPriceRange([Math.round(values[0]), Math.round(values[1])]);
              });
            }
          }
        } catch (error) {
          console.error('Plugin initialization error:', error);
        }
      }
    };

    // Handle window load event
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
      }
      initPlugins();
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
      // Cleanup slider on unmount
      if (sliderInstance && sliderInstance.noUiSlider) {
        sliderInstance.noUiSlider.destroy();
      }
    };
  }, [sliderInstance]);

  // Reset to first page when category changes
  useEffect(() => {
    setCurrentPage(0);
  }, [categorySlug]);

  // Handler to apply filters
  const handleApplyFilter = async () => {
    try {
      setFilterLoading(true);
      setCurrentPage(0); // Reset to first page when filtering
      await fetchProducts();
    } catch (err) {
      console.error('[CategoryPage] Error applying filters:', err);
    } finally {
      setFilterLoading(false);
    }
  };

  // Handler to clear filters
  const handleClearFilters = () => {
    setPriceRange([0, 800]);
    setSortBy('default');
    if (sliderInstance && sliderInstance.noUiSlider) {
      sliderInstance.noUiSlider.set([0, 800]);
    }
    setCurrentPage(0);
  };

  // Handler for sort changes
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  // Individual filter removal handlers
  const handleRemovePrice = () => {
    setPriceRange([0, 800]);
    if (sliderInstance && sliderInstance.noUiSlider) {
      sliderInstance.noUiSlider.set([0, 800]);
    }
    setCurrentPage(0);
  };

  const handleRemoveSort = () => {
    setSortBy('default');
    setCurrentPage(0);
  };

  // Category page doesn't have search or category filter (it's implicit)
  // But we keep the handlers for consistency
  const handleRemoveSearch = () => {}; // No-op
  const handleRemoveCategory = () => {
    // Navigate to all products page
    navigate('/product-listing');
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    handleClearFilters();
  };

  // Handler to add product to cart
  const handleAddToCart = async (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart!`);
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };

  return (
    <Layout>
      {/* Parallax Banner */}
      <ParallaxBanner
        imageUrl={backgroundUrl}
        speed={0.6}
        offset={-100}
        className="paralax-section-slide-data1"
      />

      {/* Category Page Start */}
      <div className="product-listing">
        <div className="container">
          <div className="row no-gutters">

            {/* Sidebar with filters */}
            <ProductSidebar
              categories={categories}
              currentCategorySlug={categorySlug}
              priceRange={priceRange}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              onClearFilters={handleClearFilters}
              onApplyFilter={handleApplyFilter}
              filterLoading={filterLoading}
              sliderInstance={sliderInstance}
            />

            {/* Main Content Area */}
            <div className="col-md-12 col-lg-8 order-2">
              {/* Filter Tags - don't show category as filter since we're already on category page */}
              <FilterTags
                priceRange={priceRange}
                sortBy={sortBy}
                selectedCategory={null}
                categories={categories}
                onRemoveSearch={handleRemoveSearch}
                onRemovePrice={handleRemovePrice}
                onRemoveSort={handleRemoveSort}
                onRemoveCategory={handleRemoveCategory}
                onClearAll={handleClearAllFilters}
              />

              {/* Category Header */}
              {category && !error && (
                <div className="category-header mb-5 text-center">
                  {category.icon && (
                    <div className="category-icon mb-3" style={{ fontSize: '4rem' }}>
                      {category.icon}
                    </div>
                  )}
                  <h1 className="heading">{category.name}</h1>
                  {category.description && (
                    <div
                      className="para_text mt-3"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(category.description) }}
                    />
                  )}
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="alert alert-warning text-center" role="alert">
                  {error}
                  <div className="mt-3">
                    <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                      View All Collections
                    </Link>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading {category?.name || 'category'} products...</p>
                </div>
              )}

              {/* Empty State */}
              {!loading && !error && products.length === 0 && category && (
                <div className="text-center py-5">
                  <p className="text-muted mb-4">No collections available in {category.name} yet.</p>
                  <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                    View All Collections
                  </Link>
                </div>
              )}

              {/* Products Grid */}
              <div className="col-12 product-listing-products">
                {!loading && !error && products.length > 0 && (
                  <div className="product-list row">
                    {products.map((product) => {
                      // Only cache-bust if product was updated via WebSocket
                      const cacheKey = productImageKeys.get(product.id);

                      return (
                        <div
                          key={product.id}
                          className="col-12 col-md-6 col-lg-4 manage-color-hover wow slideInUp"
                          data-wow-delay=".2s"
                        >
                          <div className="product-item">
                            <div className="item p-item-img">
                              <Link to={`/product-detail/${product.id}`}>
                                <img
                                  src={cacheKey ? addCacheBuster(getPrimaryImage(product), cacheKey) : getPrimaryImage(product)}
                                  alt={product.name}
                                  onError={handleImageError}
                                  loading="lazy"
                                />
                          </Link>
                          <div className="text-center d-flex justify-content-center align-items-center">
                            <button
                              className="listing-cart-icon"
                              onClick={(e) => handleAddToCart(e, product.id, product.name)}
                              disabled={cartLoading || !isInStock(product)}
                              aria-label={`Add ${product.name} to cart`}
                            >
                              <i className="fa fa-shopping-cart"></i>
                            </button>
                          </div>
                          {/* Wishlist button - top left */}
                          <div
                            className="position-absolute"
                            style={{top: '10px', left: '10px', zIndex: 10}}
                          >
                            <WishlistButton
                              productId={product.id}
                              className="product-card-wishlist-btn"
                            />
                          </div>
                          {/* Stock badge */}
                          {(() => {
                            const stockInfo = getStockDisplay(product.stock, product.stockStatus);
                            // Only show badge for out of stock or low stock
                            if (stockInfo.className === 'in-stock') return null;
                            return (
                              <span
                                className={`badge position-absolute stock-badge ${stockInfo.className}`}
                                style={{top: '10px', right: '10px'}}
                              >
                                {stockInfo.className === 'out-of-stock' ? 'Out of Stock' : `Only ${product.stock} left!`}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="p-item-detail">
                        <h4 className="text-center p-item-name">
                          <Link to={`/product-detail/${product.id}`}>
                            {product.name}
                          </Link>
                        </h4>
                        <p className="text-center p-item-price">
                          {formatPrice(product.price)}
                          {product.compareAtPrice && (
                            <span
                              className="ms-2"
                              style={{
                                fontSize: '0.85em',
                                textDecoration: 'line-through',
                                color: '#999',
                                opacity: 0.7
                              }}
                              title="Original price"
                            >
                              {formatPrice(product.compareAtPrice)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    );
                    })}
                </div>
              )}

              {/* Pagination */}
              {!loading && !error && products.length > 0 && (
                <Pagination
                  pagination={pagination}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              )}
              </div>

            </div>
          </div>
        </div>
      </div>
      {/* Category Page End */}
    </Layout>
  );
}

export default CategoryPage;
